import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp, getDocs, orderBy, addDoc } from 'firebase/firestore';
import { db, defaultDb, handleFirestoreError, OperationType } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { toArray, exportToCsv } from '../lib/utils';
import { 
  Users, 
  Settings, 
  UserPlus, 
  Shield, 
  Mail, 
  Calendar, 
  MoreVertical,
  Trash2,
  CheckCircle2,
  Clock,
  Shapes,
  BookOpen,
  ArrowRight,
  Plus,
  Activity,
  X,
  Loader2,
  Download,
  ChevronDown,
  Link as LinkIcon,
  AlertCircle
} from 'lucide-react';
import { Organisation, OrganisationMember } from '../types';

export function OrganisationDashboard() {
  const { user, memberships, currentOrg, setCurrentOrg, loading } = useAuth();
  const [members, setMembers] = useState<OrganisationMember[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'tutor' | 'learner'>('learner');
  const [activeTab, setActiveTab] = useState<'members' | 'courses' | 'settings'>('members');

  // Find the membership for the current user in the current org
  const myMembership = memberships.find(m => m.orgId === currentOrg?.id);
  const isAdmin = myMembership?.role === 'owner' || myMembership?.role === 'admin';

  const [invites, setInvites] = useState<any[]>([]);
  const [createdInviteLink, setCreatedInviteLink] = useState<string | null>(null);
  const [createdInviteId, setCreatedInviteId] = useState<string | null>(null);
  const [inviteDropdownOpen, setInviteDropdownOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);
  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      const input = document.createElement('textarea');
      input.value = text;
      input.style.position = 'fixed';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(input);
        return successful;
      } catch (err) {
        document.body.removeChild(input);
        return false;
      }
    }
  };

  const [viewingProgressCourse, setViewingProgressCourse] = useState<any | null>(null);
  const [learnerProgress, setLearnerProgress] = useState<any[]>([]);
  const [progressLoading, setProgressLoading] = useState(false);

  const loadCourseProgress = (course: any) => {
    setViewingProgressCourse(course);
    setProgressLoading(true);
    setLearnerProgress([]);
    
    // We only want progress for THIS organisation's learners.
    // Technically `courseId` could have external learners if public, 
    // so we can also filter by `organisationId: currentOrg.id`.
    // Since composite index (courseId + organisationId) may not exist,
    // we query by organisationId (to satisfy Firestore rules) and filter locally.
    const q = query(
      collection(db, 'enrollments'), 
      where('organisationId', '==', currentOrg?.id)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allOrgEnrollments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const courseEnrollments = allOrgEnrollments.filter((d: any) => d.courseId === course.id);
      setLearnerProgress(courseEnrollments);
      setProgressLoading(false);
    }, (error) => {
      console.error('Error fetching course progress:', error);
      setProgressLoading(false);
    });
  };

  useEffect(() => {
    if (!currentOrg) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Subscribe to members
    const membersQuery = query(collection(db, 'organisation_members'), where('orgId', '==', currentOrg.id));
    const unsubscribeMembers = onSnapshot(membersQuery, (snapshot) => {
      const membersData = snapshot.docs.map(doc => doc.data() as OrganisationMember);
      setMembers(membersData);
      setIsLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'organisation_members');
      setError("Failed to load organisation members. Please check your permissions.");
      setIsLoading(false);
    });

    // Subscribe to invites
    const invitesQuery = query(collection(db, 'organisation_invites'), where('organisationId', '==', currentOrg.id));
    const unsubscribeInvites = onSnapshot(invitesQuery, (snapshot) => {
      const invitesData = snapshot.docs.map(doc => doc.data());
      setInvites(invitesData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'organisation_invites');
    });

    // Subscribe to courses
    const coursesQuery = query(
      collection(db, 'courses'), 
      where('orgId', '==', currentOrg.id),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeCourses = onSnapshot(coursesQuery, (snapshot) => {
      const coursesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCourses(coursesData);
    }, (error) => {
      // It's possible there's no index yet, so we handle it gracefully
      if (error.code === 'failed-precondition') {
        console.warn("Index required for organisation courses query.");
      } else {
        handleFirestoreError(error, OperationType.LIST, 'courses');
      }
    });

    return () => {
      unsubscribeMembers();
      unsubscribeInvites();
      unsubscribeCourses();
    };
  }, [currentOrg]);

  const handleInviteAction = async (actionType: 'link' | 'email') => {
    if (!currentOrg || !isAdmin) {
        setActionError("You do not have permission to invite organisation members.");
        setTimeout(() => setActionError(null), 3000);
        return;
    }
    if (!inviteEmail.trim()) {
        setActionError("Please enter the member's email address.");
        setTimeout(() => setActionError(null), 3000);
        return;
    }
    if (!user?.email) return;

    setEmailStatus(actionType === 'email' ? 'Queueing...' : null);
    
    try {
      const inviteId = `${currentOrg.id}-${Math.random().toString(36).substring(2, 10)}`;
      const expireDate = new Date();
      expireDate.setDate(expireDate.getDate() + 7);

      const inviteData = {
        inviteId,
        organisationId: currentOrg.id,
        organisationName: currentOrg.name || currentOrg.id,
        invitedEmail: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        invitedBy: user.email,
        status: 'pending',
        createdAt: serverTimestamp(),
        expiresAt: expireDate.toISOString(),
        acceptedAt: null,
        revokedAt: null,
        deliveryMethod: actionType === 'email' ? 'email' : 'link',
        emailStatus: actionType === 'email' ? 'queued' : null
      };

      await setDoc(doc(db, 'organisation_invites', inviteId), inviteData);
      
      const link = `${window.location.origin}/accept-invite?inviteId=${inviteId}`;
      setCreatedInviteLink(link);
      setCreatedInviteId(inviteId);

      if (actionType === 'email') {
        try {
          const response = await fetch('/api/queue-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: [inviteEmail.trim().toLowerCase()],
              subject: "You have been invited to join Lumina Learn",
              text: `Hello,\n\nYou have been invited to join ${currentOrg.name} on Lumina Learn as ${inviteRole}.\n\nLumina Learn helps organisations turn documents, training materials, policies, and learning resources into structured learning experiences.\n\nClick the link below to accept your invitation:\n\n${link}\n\nIf the button or link does not work, copy and paste the invite link into your browser.\n\nThank you,\nLumina Learn App Team`,
              html: `<p>Hello,</p><p>You have been invited to join <strong>${currentOrg.name}</strong> on Lumina Learn as <strong>${inviteRole}</strong>.</p><p>Lumina Learn helps organisations turn documents, training materials, policies, and learning resources into structured learning experiences.</p><p><a href='${link}'>Accept your invitation</a></p><p>If the button or link does not work, copy and paste this link into your browser:</p><p>${link}</p><p>Thank you,<br/>Lumina Learn App Team</p>`
            })
          });

          const result = await response.json();
          
          if (!response.ok || !result.success) {
            throw new Error(result.error || "Failed to queue email");
          }
          
          await setDoc(doc(db, 'organisation_invites', inviteId), {
            mailDocumentId: result.data?.mailDocumentId || 'queued-via-api'
          }, { merge: true });

          setEmailStatus("Email Queued!");
          setTimeout(() => setEmailStatus(null), 3000);
        } catch (mailError) {
          console.error("Failed to queue invite email", mailError);
          setEmailStatus("Failed to send email. Please copy the invite link instead.");
          setTimeout(() => setEmailStatus(null), 4000);
        }
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'organisation_invites');
    }
  };

  const handleUpdateRole = async (memberEmail: string, newRole: string) => {
    if (!currentOrg || !isAdmin) return;
    
    try {
      const q = query(
        collection(db, 'organisation_members'), 
        where('orgId', '==', currentOrg.id),
        where('email', '==', memberEmail)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const oldRole = snapshot.docs[0].data().role;
        const targetUserId = snapshot.docs[0].data().userId;
        await setDoc(snapshot.docs[0].ref, { role: newRole }, { merge: true });
        
        await setDoc(doc(collection(db, 'role_audit_logs')), {
          changedUserId: targetUserId,
          changedBy: user?.uid,
          oldRole: oldRole,
          newRole: newRole,
          scope: 'organisation',
          organisationId: currentOrg.id,
          createdAt: serverTimestamp(),
          reason: 'Organisation Admin changed member role'
        });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'organisation_member_role');
    }
  };

  const handleRemoveMember = async (memberEmail: string) => {
    if (!currentOrg || !isAdmin) return;
    if (memberEmail === user?.email) return; // Can't remove yourself

    try {
      const q = query(
        collection(db, 'organisation_members'), 
        where('orgId', '==', currentOrg.id),
        where('email', '==', memberEmail)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const oldRole = snapshot.docs[0].data().role;
        const targetUserId = snapshot.docs[0].data().userId;
        await deleteDoc(snapshot.docs[0].ref);

        await setDoc(doc(collection(db, 'role_audit_logs')), {
          changedUserId: targetUserId,
          changedBy: user?.uid,
          oldRole: oldRole,
          newRole: 'removed',
          scope: 'organisation',
          organisationId: currentOrg.id,
          createdAt: serverTimestamp(),
          reason: 'Organisation Admin removed member'
        });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'organisation_member');
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!currentOrg || !isAdmin) return;

    try {
      await setDoc(doc(db, 'organisation_invites', inviteId), { status: 'revoked', revokedAt: serverTimestamp() }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'organisation_invite');
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main">
        <div className="bg-rose-500/10 border border-rose-500/20 p-8 rounded-3xl max-w-md animate-in fade-in zoom-in-95">
          <div className="h-16 w-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="h-8 w-8 text-rose-500" />
          </div>
          <h2 className="text-2xl font-bold text-text-main mb-3">Access Denied</h2>
          <p className="text-text-alt mb-8 leading-relaxed">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-4 bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-2xl transition-all shadow-xl shadow-rose-500/20 active:scale-95"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  if (loading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] bg-bg-main p-4">
        <div className="flex flex-col items-center gap-4 animate-in fade-in zoom-in-95 duration-700">
          <div className="relative">
            <div className="h-16 w-16 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
              <Shapes className="h-8 w-8 text-indigo-500 animate-pulse" />
            </div>
            <div className="absolute -top-1 -right-1 h-4 w-4 bg-emerald-500 rounded-full border-2 border-bg-main animate-bounce" />
          </div>
          <div className="text-center">
            <h3 className="text-lg font-bold text-text-main">Loading Workspace</h3>
            <p className="text-sm text-text-alt opacity-60">Synchronizing team data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (memberships.length === 0) {
    const handleCreateOrg = async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (!user) return;
      
      const orgName = newOrgName.trim() || "My New Org";
      
      const orgId = orgName.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 20) + '-' + Math.random().toString(36).substring(2, 6);
      
      try {
        // Create organisation
        await setDoc(doc(db, 'organisations', orgId), {
          id: orgId,
          name: orgName,
          ownerUserId: user.uid,
          planStatus: 'free',
          createdAt: serverTimestamp()
        });

        // Add owner as member
        const memberId = `${user.email?.toLowerCase()}_${orgId}`;
        await setDoc(doc(db, 'organisation_members', memberId), {
          orgId: orgId,
          userId: user.uid,
          email: user.email?.toLowerCase(),
          role: 'owner',
          status: 'active',
          joinedAt: serverTimestamp()
        });

        setNewOrgName('');
        setIsCreatingOrg(false);
      } catch (error) {
        handleFirestoreError(error, OperationType.WRITE, 'organisations');
      }
    };

    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main">
        <Shapes className="h-16 w-16 text-text-alt opacity-20 mb-4" />
        <h2 className="text-2xl font-bold text-text-main mb-2">No Organisations Found</h2>
        <p className="text-text-alt max-w-md mb-8">
          You are not currently a member of any organisation. If you were invited, make sure you're using the correct email address.
        </p>
        
        {isCreatingOrg ? (
          <form onSubmit={handleCreateOrg} className="w-full max-w-md flex flex-col gap-4 bg-bg-alt p-6 rounded-2xl border border-border-main">
            <h3 className="text-lg font-bold text-text-main">Create Organisation</h3>
            <div className="flex flex-col text-left">
              <label className="text-sm font-medium text-text-alt mb-1">Organisation Name</label>
              <input 
                type="text" 
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                autoFocus
                className="w-full px-4 py-2 border border-border-main bg-bg-main rounded-xl focus:ring-2 focus:ring-indigo-500 text-text-main"
                placeholder="e.g. Acme Academy"
                required
              />
            </div>
            <div className="flex justify-end gap-3 mt-2">
              <button
                type="button"
                onClick={() => setIsCreatingOrg(false)}
                className="px-4 py-2 text-text-alt hover:bg-bg-main rounded-xl transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-500/20"
              >
                Create
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setIsCreatingOrg(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-xl shadow-indigo-500/20 active:scale-95"
          >
            <Plus className="h-5 w-5" />
            Create New Organisation
          </button>
        )}
      </div>
    );
  }

  if (!currentOrg && memberships.length > 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main">
        <Shapes className="h-16 w-16 text-text-alt opacity-20 mb-4" />
        <h2 className="text-2xl font-bold text-text-main mb-2">Select an Organisation</h2>
        <p className="text-text-alt max-w-md mb-8">
          Please select an organisation from the list below to access its dashboard.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-lg">
          {toArray(memberships).map((m: any) => (
            <button
              key={m.orgId}
              onClick={() => setCurrentOrg({ id: m.orgId, name: m.orgId, ownerUserId: '', createdAt: null })}
              className="p-6 bg-bg-alt border border-border-main rounded-2xl hover:border-indigo-600 transition-all text-left group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-text-main group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{m.orgId}</h3>
                  <p className="text-xs text-text-alt capitalize mt-1">Role: {m.role}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-text-alt group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-bg-main" data-clarity-mask="true">
      {/* Header */}
      <div className="bg-bg-alt border-b border-border-main px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-indigo-600 rounded-xl flex items-center justify-center shrink-0">
              <Shapes className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <select 
                  value={currentOrg?.id || ''} 
                  onChange={(e) => {
                    const org = memberships.find(m => m.orgId === e.target.value);
                    if (org) setCurrentOrg({ id: org.orgId, name: org.orgId, ownerUserId: '', createdAt: null });
                  }}
                  className="bg-transparent text-xl font-bold text-text-main border-none p-0 focus:ring-0 cursor-pointer hover:text-indigo-500 transition-colors"
                >
                  {toArray<OrganisationMember>(memberships).map(m => (
                    <option key={m.orgId} value={m.orgId} className="bg-bg-alt text-text-main">
                      {m.orgId}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-sm text-text-alt">Organisation ID: {currentOrg?.id}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-bg-main/50 border border-border-main p-1 rounded-xl w-fit">
            <button
              onClick={() => setActiveTab('members')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'members' ? 'bg-indigo-600 text-white shadow-lg' : 'text-text-alt hover:text-text-main'
              }`}
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === 'courses' ? 'bg-indigo-600 text-white shadow-lg' : 'text-text-alt hover:text-text-main'
              }`}
            >
              Courses
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === 'settings' ? 'bg-indigo-600 text-white shadow-lg' : 'text-text-alt hover:text-text-main'
                }`}
              >
                Settings
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'members' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-text-main">Team Members</h3>
                <p className="text-sm text-text-alt">Manage access and roles for your organisation.</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors shadow-lg shadow-indigo-500/20"
                >
                  <UserPlus className="h-4 w-4" />
                  Invite Member
                </button>
              )}
            </div>

            <div className="bg-bg-alt border border-border-main rounded-2xl overflow-hidden shadow-xl">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-bg-main/30">
                    <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest">Member</th>
                    <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest">Role</th>
                    <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest">Joined</th>
                    {isAdmin && <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-main">
                  {members.map((member) => (
                    <tr key={member.email} className="hover:bg-bg-main/20 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-bg-main flex items-center justify-center text-text-main font-bold uppercase border border-border-main">
                            {member.email.charAt(0)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-text-main">{member.email}</p>
                            {member.userId === user?.uid && (
                              <span className="text-[10px] bg-indigo-500/10 text-indigo-500 px-1.5 py-0.5 rounded border border-indigo-500/20">YOU</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {isAdmin && member.userId !== user?.uid && member.role !== 'owner' ? (
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateRole(member.email, e.target.value)}
                            className="bg-bg-main border border-border-main rounded-lg text-xs py-1 px-2 text-text-main focus:ring-1 focus:ring-indigo-500"
                          >
                            <option value="admin">Admin</option>
                            <option value="tutor">Tutor</option>
                            <option value="learner">Learner</option>
                          </select>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-text-alt capitalize">
                            <Shield className="h-3 w-3" />
                            {member.role}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          member.status === 'active' 
                            ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' 
                            : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                        }`}>
                          {member.status === 'active' ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-text-alt/60">
                        {member.joinedAt?.seconds 
                          ? new Date(member.joinedAt.seconds * 1000).toLocaleDateString() 
                          : member.joinedAt instanceof Date 
                            ? member.joinedAt.toLocaleDateString()
                            : 'Pending'}
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 text-right">
                          {member.userId !== user?.uid && member.role !== 'owner' && (
                            <button
                              onClick={() => handleRemoveMember(member.email)}
                              className="p-1.5 text-text-alt hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Remove Member"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {invites.length > 0 && isAdmin && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-text-main mb-4">Pending Invites</h3>
                <div className="bg-bg-alt border border-border-main rounded-2xl overflow-hidden shadow-xl">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-bg-main/30">
                        <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest">Email</th>
                        <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest">Role</th>
                        <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest">Status / Expiry</th>
                        <th className="px-6 py-4 text-xs font-bold text-text-alt uppercase tracking-widest text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-main">
                      {invites.map((invite) => (
                        <tr key={invite.inviteId} className="hover:bg-bg-main/20 transition-colors">
                          <td className="px-6 py-4">
                            <p className="text-sm font-medium text-text-main">{invite.invitedEmail}</p>
                            <p className="text-xs text-text-alt">Sent by {invite.invitedBy}</p>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-1.5 text-xs text-text-alt capitalize">
                              <Shield className="h-3 w-3" />
                              {invite.role}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wider`}>
                              <span className={`px-2 py-0.5 rounded-full w-fit ${
                                invite.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-500' :
                                invite.status === 'revoked' ? 'bg-rose-500/10 text-rose-500' :
                                invite.status === 'expired' ? 'bg-amber-500/10 text-amber-500' : 'bg-indigo-500/10 text-indigo-500'
                              }`}>
                                {invite.status}
                              </span>
                              <span className="text-text-alt text-[9px] lowercase">
                                Expires: {new Date(invite.expiresAt).toLocaleDateString()}
                              </span>
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right flex items-center justify-end gap-2">
                            {invite.status === 'pending' && (
                              <>
                                <button
                                  onClick={async () => {
                                    const success = await copyToClipboard(`${window.location.origin}/accept-invite?inviteId=${invite.inviteId}`);
                                    if (success) {
                                      const btn = document.getElementById(`copy-btn-${invite.inviteId}`);
                                      if (btn) {
                                        const orig = btn.innerText;
                                        btn.innerText = 'Copied!';
                                        setTimeout(() => { btn.innerText = orig; }, 2000);
                                      }
                                    } else {
                                      setActionError("Failed to copy link");
                                      setTimeout(() => setActionError(null), 3000);
                                    }
                                  }}
                                  id={`copy-btn-${invite.inviteId}`}
                                  className="px-3 py-1.5 text-xs font-bold border border-border-main rounded-lg text-text-main hover:bg-bg-main transition-colors"
                                  title="Copy Link"
                                >
                                  Copy Link
                                </button>
                                <button
                                  onClick={() => handleRevokeInvite(invite.inviteId)}
                                  className="px-3 py-1.5 text-xs font-bold border border-rose-500/20 bg-rose-500/10 rounded-lg text-rose-500 hover:bg-rose-500 hover:text-white transition-colors"
                                  title="Revoke Invite"
                                >
                                  Revoke
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-text-main">Organisation Courses</h3>
                <p className="text-sm text-text-alt">Content created specifically for your organisation.</p>
              </div>
            </div>

            {viewingProgressCourse && (
                <div className="bg-bg-alt rounded-2xl border border-border-main p-6 shadow-xl animate-in fade-in slide-in-from-bottom-4">
                  <div className="flex justify-between items-center mb-6 border-b border-border-main pb-4">
                    <div>
                      <h3 className="text-xl font-bold text-text-main flex items-center gap-2">
                        <Activity className="h-5 w-5 text-emerald-500" />
                        Course Progress
                      </h3>
                      <p className="text-sm text-text-alt mt-1">
                        Viewing progress for <span className="font-semibold text-text-main">{viewingProgressCourse.title}</span>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          const cols = [
                            { key: 'learnerEmail', label: 'Learner Email' },
                            { key: 'learnerId', label: 'Learner ID' },
                            { key: 'progressPercentage', label: 'Progress %' },
                            { key: 'completedLessons', label: 'Completed Lessons' },
                            { key: 'totalLessons', label: 'Total Lessons' },
                            { key: 'lastAccessed', label: 'Last Active' },
                            { key: 'courseCompleted', label: 'Course Completed' },
                            { key: 'finalAssessmentStatus', label: 'Final Assessment Status' }
                          ];
                          exportToCsv(`org-progress-${viewingProgressCourse.title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`, learnerProgress, cols);
                        }}
                        className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-500/10 dark:hover:bg-indigo-500/20 dark:text-indigo-400 text-sm font-medium border border-indigo-500/20 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" /> Export CSV
                      </button>
                      <button 
                        onClick={() => setViewingProgressCourse(null)}
                        className="px-4 py-2 bg-bg-main hover:bg-bg-main/80 text-text-main text-sm font-medium border border-border-main rounded-lg transition-colors flex items-center gap-2"
                      >
                        <X className="h-4 w-4" /> Close
                      </button>
                    </div>
                  </div>

                  {progressLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 text-text-alt">
                      <Loader2 className="h-8 w-8 animate-spin mb-4" />
                      <p>Loading learner progress...</p>
                    </div>
                  ) : learnerProgress.length === 0 ? (
                    <div className="text-center py-16 bg-bg-main rounded-xl border border-dashed border-border-main">
                      <Users className="mx-auto h-12 w-12 text-text-alt opacity-20 mb-4" />
                      <h4 className="text-lg font-medium text-text-main mb-2">No learner progress yet</h4>
                      <p className="text-text-alt max-w-md mx-auto">
                        Progress will appear here once learners start this course.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-border-main">
                      <table className="w-full text-left border-collapse min-w-[800px]">
                        <thead>
                          <tr className="bg-bg-main border-b border-border-main text-xs font-semibold text-text-alt uppercase tracking-wider">
                            <th className="p-4">Learner</th>
                            <th className="p-4">Status</th>
                            <th className="p-4 w-1/4">Progress</th>
                            <th className="p-4">Quizzes</th>
                            <th className="p-4">Assessment</th>
                            <th className="p-4">Last Active</th>
                            <th className="p-4 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main bg-bg-alt text-sm">
                          {learnerProgress.map((prog) => {
                            const isCompleted = prog.courseCompleted === true;
                            const isStarted = prog.progressPercentage > 0 || prog.completedLessons > 0;
                            const status = isCompleted ? 'Completed' : (isStarted ? 'In Progress' : 'Not Started');
                            
                            return (
                              <tr key={prog.id} className="hover:bg-bg-main/30 transition-colors">
                                <td className="p-4">
                                  <div className="font-semibold text-text-main">{prog.learnerEmail || 'Anonymous'}</div>
                                  <div className="text-[10px] text-text-alt font-mono truncate max-w-[120px]">UID: {prog.learnerId}</div>
                                </td>
                                <td className="p-4">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border
                                    ${isCompleted ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                                      : isStarted ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' 
                                      : 'bg-bg-main text-text-alt border-border-main'}`}>
                                    {status}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <div className="flex items-center gap-3">
                                    <div className="flex-1 h-2 min-w-[100px] bg-bg-main rounded-full overflow-hidden border border-border-main/50">
                                      <div 
                                        className={`h-full ${isCompleted ? 'bg-emerald-500' : 'bg-indigo-500'} transition-all`} 
                                        style={{ width: `${prog.progressPercentage || 0}%` }}
                                      ></div>
                                    </div>
                                    <div className="text-xs font-bold text-text-main w-10 text-right">
                                      {prog.progressPercentage || 0}%
                                    </div>
                                  </div>
                                  <div className="text-[10px] text-text-alt mt-1">
                                    {prog.completedLessons || 0} of {prog.totalLessons || '?'} lessons
                                  </div>
                                </td>
                                <td className="p-4">
                                  <div className="text-sm font-semibold">{prog.quizAttempts || 0}</div>
                                  <div className="text-[10px] text-text-alt">attempts</div>
                                </td>
                                <td className="p-4">
                                  {prog.finalAssessmentStatus ? (
                                    <span className={`text-xs font-medium ${prog.finalAssessmentStatus.toLowerCase() === 'passed' ? 'text-emerald-500' : 'text-amber-500'}`}>
                                      {prog.finalAssessmentStatus}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-text-alt italic">Not Started</span>
                                  )}
                                </td>
                                <td className="p-4 text-xs text-text-alt">
                                  {prog.lastAccessed?.toDate ? prog.lastAccessed.toDate().toLocaleDateString() : 'Unknown'}
                                </td>
                                <td className="p-4 text-right">
                                  <button
                                    onClick={() => {
                                      const cols = [
                                        { key: 'learnerEmail', label: 'Learner Email' },
                                        { key: 'learnerId', label: 'Learner ID' },
                                        { key: 'progressPercentage', label: 'Progress %' },
                                        { key: 'completedLessons', label: 'Completed Lessons' },
                                        { key: 'totalLessons', label: 'Total Lessons' },
                                        { key: 'lastAccessed', label: 'Last Active' },
                                        { key: 'courseCompleted', label: 'Course Completed' },
                                        { key: 'finalAssessmentStatus', label: 'Final Assessment Status' }
                                      ];
                                      exportToCsv(`learner-progress-${prog.learnerId}-${prog.courseId}-${new Date().toISOString().split('T')[0]}.csv`, [prog], cols);
                                    }}
                                    className="p-2 text-text-alt hover:text-indigo-500 hover:bg-bg-main rounded-lg transition-colors inline-block"
                                    title="Export Learner Progress"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <div key={course.id} className="bg-bg-alt border border-border-main rounded-2xl p-6 hover:shadow-xl hover:shadow-indigo-500/5 transition-all group">
                  <div className="flex justify-between items-start mb-4">
                    <div className="h-10 w-10 bg-bg-main border border-border-main rounded-xl flex items-center justify-center text-indigo-500 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                      <BookOpen className="h-5 w-5" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-bg-main text-text-alt px-2 py-0.5 rounded border border-border-main">
                      {course.visibility}
                    </span>
                  </div>
                  <h4 className="text-lg font-bold text-text-main mb-2 line-clamp-2">{course.title}</h4>
                  <p className="text-sm text-text-alt mb-6 line-clamp-3 leading-relaxed">{course.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-border-main">
                    <div className="flex items-center gap-2 text-xs text-text-alt/60">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {course.createdAt?.seconds 
                          ? new Date(course.createdAt.seconds * 1000).toLocaleDateString() 
                          : course.createdAt instanceof Date 
                            ? course.createdAt.toLocaleDateString()
                            : 'Just now'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                       {isAdmin && (
                         <button 
                           onClick={() => loadCourseProgress(course)}
                           className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-500 hover:text-emerald-400 transition-colors bg-emerald-500/10 px-2 py-1 rounded"
                         >
                           <Activity className="h-3 w-3" />
                           Progress
                         </button>
                       )}
                       <a 
                         href={`/course/${course.id}`}
                         className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-500 hover:text-indigo-400 transition-colors ml-2"
                       >
                         View
                         <ArrowRight className="h-3 w-3" />
                       </a>
                    </div>
                  </div>
                </div>
              ))}
              {courses.length === 0 && (
                <div className="col-span-full py-20 text-center">
                  <BookOpen className="h-12 w-12 text-text-alt opacity-20 mx-auto mb-4" />
                  <p className="text-text-alt">No organisation courses found.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && isAdmin && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <h3 className="text-lg font-semibold text-text-main">Organisation Settings</h3>
            <div className="bg-bg-alt border border-border-main rounded-2xl p-8 max-w-2xl">
              <p className="text-text-alt mb-6">Settings for your organisation are limited to name updates. For data residency or custom integration requests, please contact support.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-text-alt opacity-60 uppercase tracking-widest mb-2 ml-1">Organisation Name</label>
                  <input 
                    type="text" 
                    value={currentOrg?.name} 
                    disabled 
                    className="block w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-alt cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-text-alt opacity-60 uppercase tracking-widest mb-2 ml-1">Organisation ID</label>
                  <input 
                    type="text" 
                    value={currentOrg?.id} 
                    disabled 
                    className="block w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-alt cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-bg-alt border border-border-main rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95">
            {actionError && (
              <div className="m-4 mb-0 p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex gap-3 text-rose-500 text-sm">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p>{actionError}</p>
              </div>
            )}
            {createdInviteLink ? (
              <div className="p-8 text-center">
                <div className="h-16 w-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <h3 className="text-2xl font-bold text-text-main mb-3">Invite created</h3>
                <p className="text-text-alt mb-6">
                  Copy and share this link manually with the invited user.
                </p>
                <div className="flex items-center gap-2 mb-6">
                  <input
                    type="text"
                    readOnly
                    value={createdInviteLink}
                    className="w-full px-4 py-3 bg-bg-main border border-border-main rounded-lg text-sm text-text-main focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      const success = await copyToClipboard(createdInviteLink);
                      if (success) {
                        setCopyStatus('Copied!');
                        setTimeout(() => setCopyStatus(null), 2000);
                      } else {
                        setCopyStatus('Press Ctrl+C to copy');
                        setTimeout(() => setCopyStatus(null), 3000);
                      }
                    }}
                    className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-indigo-500/20"
                  >
                    {copyStatus || 'Copy Invite Link'}
                  </button>
                  <button
                    type="button"
                    disabled={emailStatus === 'Queueing...'}
                    onClick={async () => {
                      if (!inviteEmail || !createdInviteLink || !createdInviteId || !currentOrg) return;
                      setEmailStatus('Queueing...');
                      try {
                        const response = await fetch('/api/queue-email', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            to: [inviteEmail.trim().toLowerCase()],
                            subject: "You have been invited to join Lumina Learn",
                            text: `Hello,\n\nYou have been invited to join ${currentOrg.name} on Lumina Learn as ${inviteRole}.\n\nLumina Learn helps organisations turn documents, training materials, policies, and learning resources into structured learning experiences.\n\nClick the link below to accept your invitation:\n\n${createdInviteLink}\n\nIf the button or link does not work, copy and paste the invite link into your browser.\n\nThank you,\nLumina Learn App Team`,
                            html: `<p>Hello,</p><p>You have been invited to join <strong>${currentOrg.name}</strong> on Lumina Learn as <strong>${inviteRole}</strong>.</p><p>Lumina Learn helps organisations turn documents, training materials, policies, and learning resources into structured learning experiences.</p><p><a href='${createdInviteLink}'>Accept your invitation</a></p><p>If the button or link does not work, copy and paste this link into your browser:</p><p>${createdInviteLink}</p><p>Thank you,<br/>Lumina Learn App Team</p>`
                          })
                        });

                        const result = await response.json();
                        
                        if (!response.ok || !result.success) {
                           throw new Error(result.error || "Failed to queue email");
                        }
                        
                        await setDoc(doc(db, 'organisation_invites', createdInviteId), {
                          mailDocumentId: result.data?.mailDocumentId || 'queued-via-api',
                          deliveryMethod: 'email',
                          emailStatus: 'queued'
                        }, { merge: true });

                        setEmailStatus('Email Queued!');
                        setTimeout(() => setEmailStatus(null), 3000);
                      } catch (mailError: any) {
                        console.error("Failed to queue invite email", mailError);
                        setEmailStatus('Failed to queue email');
                        setTimeout(() => setEmailStatus(null), 4000);
                      }
                    }}
                    className="w-full py-3 bg-bg-main hover:bg-border-main border border-border-main text-text-main font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    <Mail className="w-4 h-4 text-indigo-500" />
                    {emailStatus && emailStatus !== 'Queueing...' ? emailStatus : (emailStatus === 'Queueing...' ? 'Queueing...' : 'Email Invite Link')}
                  </button>
                  <button
                    onClick={() => {
                      setShowInviteModal(false);
                      setCreatedInviteLink(null);
                      setCreatedInviteId(null);
                      setInviteEmail('');
                    }}
                    className="w-full py-3 bg-transparent text-text-alt hover:text-text-main font-bold rounded-xl transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
            <form>
              <div className="p-8">
                <div className="h-12 w-12 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-indigo-500 mb-6">
                  <UserPlus className="h-6 w-6" />
                </div>
                <h3 className="text-2xl font-bold text-text-main mb-2">Invite Member</h3>
                <p className="text-text-alt text-sm mb-6">Create a secure invite link to share with your new team member.</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-text-alt/60 uppercase tracking-widest mb-2 ml-1">Email Address</label>
                    <input
                      type="email"
                      required
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="e.g. coworker@company.com"
                      className="block w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main placeholder-text-alt/40 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-bold text-text-alt/60 uppercase tracking-widest mb-2 ml-1">Role</label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['admin', 'tutor', 'learner'] as const).map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => setInviteRole(role)}
                          className={`px-3 py-2 rounded-lg text-xs font-bold transition-all border ${
                            inviteRole === role
                              ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg'
                              : 'bg-bg-main border-border-main text-text-alt hover:border-text-alt/40'
                          }`}
                        >
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-8 py-6 bg-bg-main/50 border-t border-border-main flex justify-end gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-text-alt hover:text-text-main"
                >
                  Cancel
                </button>
                <div className="relative">
                  <div className="flex rounded-xl overflow-hidden shadow-lg shadow-indigo-500/20">
                    <button
                      type="button"
                      onClick={() => setInviteDropdownOpen(!inviteDropdownOpen)}
                      className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-all flex items-center gap-2"
                    >
                      Create Invite
                      <ChevronDown className={`w-4 h-4 transition-transform ${inviteDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                  
                  {inviteDropdownOpen && (
                    <div className="absolute right-0 bottom-full mb-2 w-48 bg-bg-alt border border-border-main rounded-xl shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95">
                      <button
                        type="button"
                        onClick={(e) => {
                          const form = e.currentTarget.closest('form');
                          if (form && !form.reportValidity()) return;
                          setInviteDropdownOpen(false);
                          handleInviteAction('link');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-text-main hover:bg-bg-main transition-colors border-b border-border-main flex items-center gap-2"
                      >
                         <LinkIcon className="h-4 w-4 text-indigo-500" />
                         Copy Invite Link
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          const form = e.currentTarget.closest('form');
                          if (form && !form.reportValidity()) return;
                          setInviteDropdownOpen(false);
                          handleInviteAction('email');
                        }}
                        className="w-full text-left px-4 py-3 text-sm text-text-main hover:bg-bg-main transition-colors flex items-center gap-2"
                      >
                         <Mail className="h-4 w-4 text-indigo-500" />
                         Email Invite Link
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
